import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LogOut, Plus, Trash2, Pencil, X } from "lucide-react";

const TABS = ["enquiries", "members", "pathways", "resources"];

const EMPTY_PATHWAY = { level: 1, title: "", subtitle: "", overview: "", outcomes: [], projects: [], duration: "", skills: [], future_pathways: [], image: "" };
const EMPTY_RESOURCE = { title: "", type: "guide", category: "", description: "", url: "", image: "", featured: false };

const toLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [members, setMembers] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [resources, setResources] = useState([]);
  const [stats, setStats] = useState({});
  const [editing, setEditing] = useState(null); // {kind, data}

  useEffect(() => {
    if (ready && !user) nav("/admin/login");
  }, [ready, user, nav]);

  const loadAll = useCallback(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/pathways").then((r) => setPathways(r.data)).catch(() => {});
    api.get("/resources").then((r) => setResources(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  if (!user) return null;

  const openNew = (kind) => setEditing({ kind, data: kind === "pathway" ? { ...EMPTY_PATHWAY } : { ...EMPTY_RESOURCE } });
  const openEdit = (kind, data) => setEditing({ kind, data: { ...data } });

  const save = async () => {
    const { kind, data } = editing;
    const path = kind === "pathway" ? "pathways" : "resources";
    try {
      if (data.id) await api.put(`/${path}/${data.id}`, data);
      else await api.post(`/${path}`, data);
      toast.success("Saved");
      setEditing(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (kind, id) => {
    if (!window.confirm("Delete this item?")) return;
    const path = kind === "pathway" ? "pathways" : "resources";
    await api.delete(`/${path}/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-rainforest text-cream">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full bg-ochre" />
            <span className="font-serif text-xl">Academy CMS</span>
          </div>
          <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout" className="flex items-center gap-2 text-cream/70 hover:text-cream text-sm">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Stat label="Enquiries" value={stats.enquiries} />
          <Stat label="Members" value={stats.members} />
          <Stat label="Pathways" value={stats.pathways} />
          <Stat label="Resources" value={stats.resources} />
        </div>

        <div className="flex gap-2 mb-8 border-b border-charcoal/10">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`px-5 py-3 text-sm capitalize transition-colors ${tab === t ? "border-b-2 border-ochre text-rainforest" : "text-charcoal/50"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (
          <div className="space-y-3" data-testid="admin-enquiries">
            {enquiries.map((e) => (
              <div key={e.id} className="border border-charcoal/10 p-5" data-testid="admin-enquiry-row">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <span className="text-xs bg-ochre text-cream px-2 py-1 capitalize">{e.form_type}</span>
                  <span className="font-medium text-charcoal">{e.name}</span>
                  {e.organisation && <span className="text-charcoal/50 text-sm">· {e.organisation}</span>}
                  <span className="text-charcoal/50 text-sm">· {e.email}</span>
                  {e.phone && <span className="text-charcoal/50 text-sm">· {e.phone}</span>}
                  {e.location && <span className="text-charcoal/50 text-sm">· {e.location}</span>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm text-charcoal/70">
                  {e.interest && <p><span className="text-charcoal/40">Interest: </span>{e.interest}</p>}
                  {e.audience && <p><span className="text-charcoal/40">For: </span>{e.audience}</p>}
                  {e.current_activity && <p><span className="text-charcoal/40">Activity: </span>{e.current_activity}</p>}
                  {e.timeframe && <p><span className="text-charcoal/40">Timeframe: </span>{e.timeframe}</p>}
                  {e.goals && <p className="md:col-span-2"><span className="text-charcoal/40">Goals: </span>{e.goals}</p>}
                  {e.seed_bank_supports && <p className="md:col-span-2"><span className="text-charcoal/40">Seed bank supports: </span>{e.seed_bank_supports}</p>}
                  {e.priority_species && <p className="md:col-span-2"><span className="text-charcoal/40">Priority species: </span>{e.priority_species}</p>}
                </div>
              </div>
            ))}
            {enquiries.length === 0 && <p className="py-10 text-center text-charcoal/40">No enquiries yet.</p>}
          </div>
        )}

        {tab === "members" && (
          <div className="overflow-x-auto" data-testid="admin-members">
            <table className="w-full text-left text-sm">
              <thead className="text-charcoal/50 border-b border-charcoal/10">
                <tr><th className="py-3 pr-4">Name</th><th className="pr-4">Email</th><th className="pr-4">Region</th><th className="pr-4">Interest</th><th className="pr-4">Tier</th></tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-charcoal/8" data-testid="admin-member-row">
                    <td className="py-3 pr-4 font-medium text-charcoal">{m.name}</td>
                    <td className="pr-4 text-charcoal/70">{m.email}</td>
                    <td className="pr-4 text-charcoal/70">{m.region || "—"}</td>
                    <td className="pr-4 text-charcoal/70">{m.interest || "—"}</td>
                    <td className="pr-4"><span className="text-xs bg-sandstone px-2 py-1">{m.tier}</span></td>
                  </tr>
                ))}
                {members.length === 0 && <tr><td colSpan="5" className="py-10 text-center text-charcoal/40">No members yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "pathways" && (
          <ListManager kind="pathway" items={pathways} onNew={() => openNew("pathway")} onEdit={(d) => openEdit("pathway", d)} onDelete={(id) => remove("pathway", id)}
            render={(p) => <><span className="font-serif text-ochre text-xl w-8">{p.level}</span><span className="font-serif text-lg text-rainforest">{p.title}</span></>} />
        )}

        {tab === "resources" && (
          <ListManager kind="resource" items={resources} onNew={() => openNew("resource")} onEdit={(d) => openEdit("resource", d)} onDelete={(id) => remove("resource", id)}
            render={(r) => <><span className="text-xs bg-sandstone px-2 py-1">{r.type}</span><span className="font-serif text-lg text-rainforest">{r.title}</span></>} />
        )}
      </div>

      {editing && (
        <Editor editing={editing} setEditing={setEditing} onSave={save} />
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="border border-charcoal/10 p-6">
    <p className="font-serif text-4xl text-rainforest">{value ?? "—"}</p>
    <p className="overline text-charcoal/40 mt-2">{label}</p>
  </div>
);

const ListManager = ({ kind, items, onNew, onEdit, onDelete, render }) => (
  <div>
    <button onClick={onNew} data-testid={`admin-new-${kind}`} className="flex items-center gap-2 bg-rainforest text-cream px-5 py-3 text-sm mb-6 hover:bg-eucalyptus transition-colors">
      <Plus size={16} /> New {kind}
    </button>
    <div className="space-y-2">
      {items.map((it) => (
        <div key={it.id} className="flex items-center gap-4 border border-charcoal/10 px-5 py-4" data-testid={`admin-${kind}-row`}>
          {render(it)}
          <div className="ml-auto flex gap-2">
            <button onClick={() => onEdit(it)} data-testid={`admin-edit-${kind}`} className="text-charcoal/50 hover:text-eucalyptus"><Pencil size={16} /></button>
            <button onClick={() => onDelete(it.id)} data-testid={`admin-delete-${kind}`} className="text-charcoal/50 hover:text-destructive"><Trash2 size={16} /></button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

function Editor({ editing, setEditing, onSave }) {
  const { kind, data } = editing;
  const set = (k, v) => setEditing((e) => ({ ...e, data: { ...e.data, [k]: v } }));
  const inputCls = "w-full bg-cream border border-charcoal/20 px-3 py-2 text-charcoal focus:outline-none focus:border-ochre";

  return (
    <div className="fixed inset-0 z-[60] bg-charcoal/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-editor">
      <div className="bg-cream w-full max-w-2xl my-8 p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl text-rainforest capitalize">{data.id ? "Edit" : "New"} {kind}</h3>
          <button onClick={() => setEditing(null)} data-testid="admin-editor-close"><X /></button>
        </div>

        <div className="space-y-4">
          {kind === "pathway" ? (
            <>
              <Row><L>Level</L><input type="number" value={data.level} onChange={(e) => set("level", Number(e.target.value))} data-testid="editor-level" className={inputCls} /></Row>
              <Row><L>Title</L><input value={data.title} onChange={(e) => set("title", e.target.value)} data-testid="editor-title" className={inputCls} /></Row>
              <Row><L>Subtitle</L><input value={data.subtitle} onChange={(e) => set("subtitle", e.target.value)} className={inputCls} /></Row>
              <Row><L>Overview</L><textarea rows={3} value={data.overview} onChange={(e) => set("overview", e.target.value)} className={inputCls} /></Row>
              <Row><L>Duration</L><input value={data.duration} onChange={(e) => set("duration", e.target.value)} className={inputCls} /></Row>
              <Row><L>Image URL</L><input value={data.image} onChange={(e) => set("image", e.target.value)} className={inputCls} /></Row>
              <Row><L>Outcomes (one/line)</L><textarea rows={3} value={toLines(data.outcomes)} onChange={(e) => set("outcomes", fromLines(e.target.value))} className={inputCls} /></Row>
              <Row><L>Projects (one/line)</L><textarea rows={2} value={toLines(data.projects)} onChange={(e) => set("projects", fromLines(e.target.value))} className={inputCls} /></Row>
              <Row><L>Skills (one/line)</L><textarea rows={2} value={toLines(data.skills)} onChange={(e) => set("skills", fromLines(e.target.value))} className={inputCls} /></Row>
              <Row><L>Future pathways (one/line)</L><textarea rows={2} value={toLines(data.future_pathways)} onChange={(e) => set("future_pathways", fromLines(e.target.value))} className={inputCls} /></Row>
            </>
          ) : (
            <>
              <Row><L>Title</L><input value={data.title} onChange={(e) => set("title", e.target.value)} data-testid="editor-title" className={inputCls} /></Row>
              <Row><L>Type</L>
                <select value={data.type} onChange={(e) => set("type", e.target.value)} className={inputCls}>
                  {["guide", "manual", "template", "paper", "video", "podcast"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Row>
              <Row><L>Category</L><input value={data.category} onChange={(e) => set("category", e.target.value)} className={inputCls} /></Row>
              <Row><L>Description</L><textarea rows={3} value={data.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></Row>
              <Row><L>URL</L><input value={data.url} onChange={(e) => set("url", e.target.value)} className={inputCls} /></Row>
              <Row><L>Image URL</L><input value={data.image} onChange={(e) => set("image", e.target.value)} className={inputCls} /></Row>
              <Row><L>Featured</L><input type="checkbox" checked={data.featured} onChange={(e) => set("featured", e.target.checked)} className="w-5 h-5 accent-ochre" /></Row>
            </>
          )}
        </div>

        <div className="flex gap-3 mt-8">
          <button onClick={onSave} data-testid="admin-editor-save" className="bg-ochre text-cream px-6 py-3 text-sm hover:bg-copper transition-colors">Save</button>
          <button onClick={() => setEditing(null)} className="border border-charcoal/20 text-charcoal px-6 py-3 text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

const Row = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-[170px_1fr] gap-2 sm:items-center">{children}</div>;
const L = ({ children }) => <span className="overline text-charcoal/40">{children}</span>;
